/**
 * Parseo tolerante del JSON que los admins pegan desde ChatGPT.
 * Los modelos a veces devuelven comillas dobles sin escapar dentro de los
 * valores ("en":"x2 "BATTLE INTRO" ANIMATIONS") o las etiquetas HTML con una
 * barra invertida delante ("\<br>\<span>"), que rompen JSON.parse.
 */

// Heuristica: dentro de un string, una comilla solo cierra si el siguiente
// caracter no-espacio es , } ] : o el fin del texto; el resto se escapan.
function escapeInnerJsonQuotes(text) {
  let out = '';
  let inString = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (!inString) {
      if (char === '"') inString = true;
      out += char;
      continue;
    }
    if (char === '\\') {
      out += char + (text[i + 1] || '');
      i += 1;
      continue;
    }
    if (char !== '"') {
      out += char;
      continue;
    }
    let j = i + 1;
    while (j < text.length && /\s/.test(text[j])) j += 1;
    const next = text[j] || '';
    if (next === ',' || next === '}' || next === ']' || next === ':' || next === '') {
      inString = false;
      out += char;
    } else {
      out += '\\"';
    }
  }
  return out;
}

// Los modelos tambien escapan a veces el HTML como si fuera markdown
// ("\<strong>", "\</br>\<span>", "w1\_title"): una barra invertida delante de
// un caracter que no es un escape de JSON rompe JSON.parse. Se quita la barra
// y se deja el caracter; los escapes validos (\" \\ \/ \b \f \n \r \t \uXXXX)
// no se tocan.
const JSON_ESCAPES = new Set(['"', '\\', '/', 'b', 'f', 'n', 'r', 't']);

function dropInvalidEscapes(text) {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char !== '\\') {
      out += char;
      continue;
    }
    const next = text[i + 1] || '';
    const valid = next === 'u'
      ? /^[0-9a-f]{4}$/i.test(text.slice(i + 2, i + 6))
      : JSON_ESCAPES.has(next);
    if (valid) {
      out += char + next;
      i += 1;
    }
  }
  return out;
}

function describeJsonError(error, raw) {
  const message = String(error?.message || error || 'JSON invalido');
  const position = Number(message.match(/position (\d+)/i)?.[1]);
  if (!Number.isFinite(position)) return `JSON invalido: ${message}`;
  const from = Math.max(0, position - 40);
  const excerpt = raw.slice(from, Math.min(raw.length, position + 40)).replace(/\s+/g, ' ');
  return `JSON invalido cerca de: ...${excerpt}...`;
}

export function extractJsonObject(value) {
  const text = String(value || '')
    .replace(/^```(?:json)?/i, '')
    .replace(/```$/i, '')
    .trim();
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('No se ha encontrado un JSON valido.');
  }
  const raw = text.slice(start, end + 1);
  try {
    return JSON.parse(raw);
  } catch {
    // Sin las barras de mas; lo que siga fallando es el error que se ensena.
    const unescaped = dropInvalidEscapes(raw);
    try {
      return JSON.parse(unescaped);
    } catch (parseError) {
      try {
        return JSON.parse(escapeInnerJsonQuotes(unescaped));
      } catch {
        throw new Error(describeJsonError(parseError, unescaped));
      }
    }
  }
}
